import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import NavBar from '../components/NavBar';
import { useAuth } from '../AuthContext';

const ROLE_INFO = {
  admin:   { color: '#00e676', label: 'Admin',   desc: 'Full access + user management' },
  manager: { color: '#f59e0b', label: 'Manager', desc: 'Manage global destinations + view all streams' },
  dj:      { color: '#9146ff', label: 'DJ',      desc: 'Stream only + own personal destinations' },
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'dj', can_create_destinations: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    try { const { data } = await api.get('/auth/users'); setUsers(data); }
    catch (err) { if (err.response?.status === 403) navigate('/dashboard'); }
  }, [navigate]);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    loadUsers();
  }, [user, navigate, loadUsers]);

  const openCreate = () => {
    setForm({ name: '', email: '', password: '', role: 'dj', can_create_destinations: true });
    setError(''); setSuccess(''); setModal('create');
  };

  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: '', role: u.role, can_create_destinations: u.can_create_destinations });
    setError(''); setSuccess(''); setModal(u);
  };

  const handleSave = async () => {
    setError(''); setSuccess('');
    if (!form.name || !form.email) { setError('Name and email required'); return; }
    if (modal === 'create' && !form.password) { setError('Password required'); return; }
    if (form.password && form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      if (modal === 'create') {
        await api.post('/auth/users', form);
        setSuccess(`Account created for ${form.email}`);
      } else {
        const payload = { name: form.name, email: form.email, role: form.role, can_create_destinations: form.can_create_destinations };
        if (form.password) payload.password = form.password;
        await api.patch(`/auth/users/${modal.id}`, payload);
        setSuccess('User updated successfully');
      }
      loadUsers();
      setTimeout(() => { setModal(null); setSuccess(''); }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setLoading(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete account for ${u.name}?\nThis will also delete their stream history.`)) return;
    try { await api.delete(`/auth/users/${u.id}`); loadUsers(); }
    catch (err) { alert(err.response?.data?.error || 'Failed to delete'); }
  };

  const s = {
    page: { minHeight: '100vh', background: 'var(--bg)' },
    nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', height: '58px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', position: 'sticky', top: 0, zIndex: 10 },
    navLogo: { display: 'flex', alignItems: 'center', gap: '9px', fontWeight: '800', fontSize: '17px' },
    navDot: { width: '9px', height: '9px', borderRadius: '50%', background: 'var(--signal)', boxShadow: '0 0 8px var(--signal)' },
    navRight: { display: 'flex', alignItems: 'center', gap: '10px' },
    navBtn: (highlight) => ({ fontSize: '13px', padding: '6px 14px', border: `1px solid ${highlight ? 'rgba(0,230,118,0.3)' : 'var(--border)'}`, borderRadius: '7px', background: 'transparent', color: highlight ? 'var(--signal)' : 'var(--text-muted)', cursor: 'pointer' }),
    main: { maxWidth: '960px', margin: '0 auto', padding: '40px 24px' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' },
    title: { fontSize: '24px', fontWeight: '800', letterSpacing: '-0.5px' },
    sub: { fontSize: '14px', color: 'var(--text-muted)', marginTop: '4px' },
    addBtn: { background: 'var(--signal)', color: '#000', fontWeight: '700', fontSize: '13px', borderRadius: '8px', padding: '9px 18px', cursor: 'pointer', border: 'none' },
    // Role legend
    legend: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
    legendItem: (color) => ({ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)' }),
    legendDot: (color) => ({ width: '8px', height: '8px', borderRadius: '50%', background: color }),
    // Table
    tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '11px 16px', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', borderBottom: '1px solid var(--border)', background: 'var(--surface2)' },
    tr: (i) => ({ background: i % 2 === 0 ? 'var(--surface)' : 'transparent', borderBottom: '1px solid var(--border)' }),
    td: { padding: '14px 16px', fontSize: '14px', verticalAlign: 'middle' },
    roleBadge: (role) => {
      const info = ROLE_INFO[role] || ROLE_INFO.dj;
      return { fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '20px', background: `${info.color}15`, color: info.color, border: `1px solid ${info.color}30` };
    },
    permBadge: (on) => ({ fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '4px', background: on ? 'rgba(0,230,118,0.08)' : 'var(--surface2)', color: on ? 'var(--signal)' : 'var(--text-muted)', border: `1px solid ${on ? 'rgba(0,230,118,0.2)' : 'var(--border)'}` }),
    actions: { display: 'flex', gap: '6px' },
    editBtn: { background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '7px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    delBtn: { background: 'var(--danger-bg)', border: '1px solid rgba(255,77,77,0.2)', color: 'var(--danger)', borderRadius: '7px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    mono: { fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text-muted)' },
    // Modal
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '24px' },
    modal: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '36px', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto' },
    modalTitle: { fontSize: '20px', fontWeight: '800', marginBottom: '24px' },
    field: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '7px' },
    roleGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' },
    roleBtn: (sel, color) => ({ padding: '10px 8px', borderRadius: '8px', cursor: 'pointer', textAlign: 'center', border: `1px solid ${sel ? color : 'var(--border)'}`, background: sel ? `${color}15` : 'var(--surface2)', color: sel ? color : 'var(--text-dim)', fontWeight: sel ? '700' : '500', fontSize: '12px', transition: 'all 0.15s' }),
    roleDesc: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px', padding: '8px 12px', background: 'var(--surface2)', borderRadius: '7px', border: '1px solid var(--border)' },
    toggle: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'var(--surface2)', borderRadius: '9px', border: '1px solid var(--border)', cursor: 'pointer' },
    toggleLabel: { flex: 1 },
    toggleTitle: { fontSize: '13px', fontWeight: '600' },
    toggleSub: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' },
    toggleSwitch: (on) => ({ width: '36px', height: '20px', borderRadius: '10px', background: on ? 'var(--signal)' : 'var(--border)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }),
    toggleKnob: (on) => ({ position: 'absolute', top: '3px', left: on ? '19px' : '3px', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }),
    row: { display: 'flex', gap: '12px', marginTop: '8px' },
    saveBtn: { flex: 1, padding: '12px', background: 'var(--signal)', color: '#000', fontWeight: '700', fontSize: '14px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer' },
    cancelBtn: { flex: 1, padding: '12px', background: 'var(--surface2)', color: 'var(--text)', fontWeight: '600', fontSize: '14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer' },
    errorBox: { background: 'var(--danger-bg)', border: '1px solid rgba(255,77,77,0.25)', color: 'var(--danger)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' },
    successBox: { background: 'rgba(0,230,118,0.07)', border: '1px solid rgba(0,230,118,0.2)', color: 'var(--signal)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' },
  };

  return (
        <NavBar>
    <div style={s.page}>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.title}>User Management</div>
            <div style={s.sub}>Dennery FM 88.9 — {users.length} account{users.length !== 1 ? 's' : ''}</div>
          </div>
          <button style={s.addBtn} onClick={openCreate}>+ Add user</button>
        </div>

        {/* Role legend */}
        <div style={s.legend}>
          {Object.entries(ROLE_INFO).map(([key, info]) => (
            <div key={key} style={s.legendItem(info.color)}>
              <div style={s.legendDot(info.color)} />
              <strong style={{ color: info.color }}>{info.label}</strong> — {info.desc}
            </div>
          ))}
        </div>

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Name</th>
                <th style={s.th}>Email</th>
                <th style={s.th}>Role</th>
                <th style={s.th}>Personal Dests</th>
                <th style={s.th}>Streams</th>
                <th style={s.th}>Last Live</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={s.tr(i)}>
                  <td style={s.td}>
                    <div style={{ fontWeight: '600' }}>{u.name}</div>
                    {u.id === user.id && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>You</div>}
                  </td>
                  <td style={s.td}>{u.email}</td>
                  <td style={s.td}><span style={s.roleBadge(u.role)}>{u.role}</span></td>
                  <td style={s.td}>
                    <span style={s.permBadge(u.can_create_destinations)}>
                      {u.can_create_destinations ? '✓ Allowed' : '✕ Disabled'}
                    </span>
                  </td>
                  <td style={s.td}>{u.total_streams || 0}</td>
                  <td style={s.td}>
                    {u.last_stream
                      ? new Date(u.last_stream).toLocaleDateString()
                      : <span style={{ color: 'var(--text-muted)' }}>Never</span>}
                  </td>
                  <td style={s.td}>
                    <div style={s.actions}>
                      <button style={s.editBtn} onClick={() => openEdit(u)}>Edit</button>
                      {u.id !== user.id && (
                        <button style={s.delBtn} onClick={() => handleDelete(u)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div style={s.modal}>
            <div style={s.modalTitle}>
              {modal === 'create' ? 'Add user' : `Edit ${modal.name}`}
            </div>

            {error && <div style={s.errorBox}>{error}</div>}
            {success && <div style={s.successBox}>✓ {success}</div>}

            <div style={s.field}>
              <label style={s.label}>Name</label>
              <input placeholder="Full name" value={form.name}
                onChange={e => setForm({...form, name: e.target.value})} />
            </div>

            <div style={s.field}>
              <label style={s.label}>Email</label>
              <input type="email" placeholder="email@denneryfm.com" value={form.email}
                onChange={e => setForm({...form, email: e.target.value})} />
            </div>

            <div style={s.field}>
              <label style={s.label}>
                {modal === 'create' ? 'Password' : 'New password (leave blank to keep)'}
              </label>
              <input type="password" placeholder="••••••••" value={form.password}
                onChange={e => setForm({...form, password: e.target.value})} />
            </div>

            <div style={s.field}>
              <label style={s.label}>Role</label>
              <div style={s.roleGrid}>
                {Object.entries(ROLE_INFO).map(([key, info]) => (
                  <button key={key} style={s.roleBtn(form.role === key, info.color)}
                    onClick={() => setForm({...form, role: key})}>
                    {info.label}
                  </button>
                ))}
              </div>
              <div style={s.roleDesc}>
                {ROLE_INFO[form.role]?.desc}
              </div>
            </div>

            <div style={s.field}>
              <label style={s.label}>Permissions</label>
              <div style={s.toggle}
                onClick={() => setForm({...form, can_create_destinations: !form.can_create_destinations})}>
                <div style={s.toggleLabel}>
                  <div style={s.toggleTitle}>Can create personal destinations</div>
                  <div style={s.toggleSub}>Allow this user to add their own private stream destinations</div>
                </div>
                <div style={s.toggleSwitch(form.can_create_destinations)}>
                  <div style={s.toggleKnob(form.can_create_destinations)} />
                </div>
              </div>
            </div>

            <div style={s.row}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button style={s.saveBtn} onClick={handleSave} disabled={loading}>
                {loading ? 'Saving…' : modal === 'create' ? 'Create account' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </NavBar>
  );
}
